// src/pages/document/DocumentNumberingSchemeListPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
  Badge,
} from '@react/components';

const PATTERN_LABELS: Record<string, string> = {
  SEQUENTIAL: 'Sequential',
  YEAR_SEQUENTIAL: 'Year+Sequential',
  CATEGORY_SEQUENTIAL: 'Category+Sequential',
  YEAR_CATEGORY: 'Year+Category',
};

/**
 * Document numbering scheme list page.
 */
export const DocumentNumberingSchemeListPage = observer(() => {
  const store = useDocument();
  const { t } = useI18n();
  const [items, setItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await store.ListNumberingSchemes();
        const schemes = res?.schemes?.schemes;
        setItems(Array.isArray(schemes) ? schemes : []);
      } catch (err: unknown) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/documents" className="text-muted-foreground hover:text-foreground">
              {t('document.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('document.numberingSchemes')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('document.numberingSchemes')}</h1>
        </div>
        <Link to="/documents/numbering-schemes/new">
          <Button>{t('common.create')}</Button>
        </Link>
      </div>

      {error ? (
        <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />
      ) : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Prefix</TableHead>
              <TableHead>Separator</TableHead>
              <TableHead>Pattern</TableHead>
              <TableHead>Counter Digits</TableHead>
              <TableHead>Next Number</TableHead>
              <TableHead>Active</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableEmpty colSpan={7} message={t('document.noItems')} />
            ) : (
              items.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    {String(item.name ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.prefix ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.separator ?? '')}
                  </TableCell>
                  <TableCell>
                    {PATTERN_LABELS[item.pattern] || String(item.pattern ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.counter_digits ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.next_number ?? '')}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={item.is_active ? 'success' : 'secondary'}
                      size="sm"
                    >
                      {item.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default DocumentNumberingSchemeListPage;
